import { useQuery } from '@tanstack/react-query'
import { Badge, Button, Callout, Code, DataList, Flex, HoverCard, Strong, Table, Text } from '@radix-ui/themes'
import { listDatapointsPayloadByDeviceID } from '~/features/stations/datasources-service'
import { useState } from 'react'
import { MdOutlineWifiTethering } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { MeterDataSource } from '@willie/schemas'
import { useCurrentOrg } from '~/features/orgnisations/hooks/useCurrentOrg'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { DateTime } from 'luxon'

interface MeterSourcePreviewProps {
  meter: MeterDataSource
}

export const MeterSourcePreview = ({ meter }: MeterSourcePreviewProps) => {
  const { t } = useTranslation(['sites', 'common'])
  const org = useCurrentOrg()
  const locale = useCurrentLanguage()
  const [nDataPoints, setNDataPoints] = useState(5)

  const {
    data: dataPoints = [],
    error: dataPointsError,
    isLoading: dataPointsLoading
  } = useQuery({
    queryKey: ['data-lake', { deviceId: meter.config.deviceID }],
    retry: false,
    queryFn: () =>
      listDatapointsPayloadByDeviceID({
        orgID: org.id,
        deviceID: meter.config.deviceID,
        mappingQuery: meter.config.mappingQuery,
        limit: 100
      })
  })

  return (
    <Flex gap={'3'} direction={'column'} mt={'4'}>
      <HoverCard.Root>
        <HoverCard.Trigger>
          <Flex align={'center'} gap={'2'}>
            <MdOutlineWifiTethering size={18} color="var(--mint-11)" />
            <Text size="3" weight={'medium'}>
              {meter.config.deviceID}
            </Text>
          </Flex>
        </HoverCard.Trigger>
        <HoverCard.Content>
          <DataList.Root>
            <DataList.Item>
              <DataList.Label>{t('stations.meter.details.deviceId')}</DataList.Label>
              <DataList.Value>
                <Badge>{meter.config.deviceID}</Badge>
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>Device Maker</DataList.Label>
              <DataList.Value>{meter.config.maker}</DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>{t('stations.meter.details.readingMethod')}</DataList.Label>
              <DataList.Value>{meter.readingMethod}</DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>{t('stations.meter.details.measurementUnit')}</DataList.Label>
              <DataList.Value>{meter.unit}</DataList.Value>
            </DataList.Item>
          </DataList.Root>
        </HoverCard.Content>
      </HoverCard.Root>
      {!dataPointsLoading && !!dataPointsError && (
        <Callout.Root color="red">
          <Callout.Text>
            <Text as="p">
              {dataPointsError.message} - {t('stations.meter.form.datapoints.errorMessage')}
            </Text>
          </Callout.Text>
        </Callout.Root>
      )}
      {!dataPointsError && (
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>
                <Strong>{t('stations.meter.form.datapoints.timestamp')}</Strong>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                <Strong>{t('stations.meter.form.datapoints.value')}</Strong>
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {dataPoints.slice(0, nDataPoints).map((dp) => (
              <Table.Row key={`${JSON.stringify(dp)}`}>
                <Table.Cell>
                  {Math.abs(dp.dateTime.diffNow('days').days) < 1
                    ? t('todayAt', {
                        time: dp.dateTime.toLocaleString(DateTime.TIME_SIMPLE, { locale }),
                        ns: 'common'
                      })
                    : dp.dateTime?.toLocaleString(DateTime.DATETIME_MED, { locale })}
                </Table.Cell>
                <Table.Cell maxWidth={'500px'}>
                  <Code color="gray">{JSON.stringify(dp.value)}</Code>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      <Flex justify={'center'}>
        <Button
          variant="ghost"
          onClick={() => setNDataPoints(nDataPoints + 10)}
          disabled={nDataPoints >= dataPoints.length}
        >
          {t('stations.meter.buttons.viewMore')}
        </Button>
      </Flex>
    </Flex>
  )
}
